import { zodResolver } from "@hookform/resolvers/zod";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useAtom, useAtomValue } from "jotai";
import { useState, type JSX } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";

import Mail from "@repo/icons/default/Mail";
import { CpsInput } from "corpus";

import { AuthPageLayout } from "@/components/auth/auth-page-layout";
import { AuthFlowContinueButton } from "@/components/ui/auth-flow/auth-flow-continue-button";
import { FlowHeader } from "@/components/ui/flow-header/flow-header";
import {
  FormControl,
  FormField,
  FormHandlerSubmit,
  FormItem,
  FormRoot,
} from "@/components/ui/form/form";
import { PageDescription, PageTitle } from "@/components/ui/layout/page/page";
import { usePasswordReset } from "@/hooks/use-password-reset";
import { useStytchErrorDrawer } from "@/hooks/use-stytch-error-drawer";
import { passwordResetAtom } from "@/lib/atoms/password-reset-atom";
import { returnToAtom } from "@/lib/atoms/return-to-atom";

const forgotPasswordSchema = z.object({
  // Ver credentials-form.tsx: o .min(1) precisa rodar antes do formato para o
  // campo vazio exibir "E-mail obrigatório", como no zod v3.
  email: z.string().min(1, "E-mail obrigatório").pipe(z.email("E-mail inválido")),
});

type ForgotPasswordFormValues = z.infer<typeof forgotPasswordSchema>;

const ForgotPasswordPage = (): JSX.Element => {
  const [passwordReset, setPasswordReset] = useAtom(passwordResetAtom);

  // Mantém o e-mail já preenchido na tela de login (ou em uma visita anterior
  // a esta tela) em vez de pedir que seja digitado de novo.
  const form = useForm<ForgotPasswordFormValues>({
    resolver: zodResolver(forgotPasswordSchema),
    defaultValues: { email: passwordReset.email },
    mode: "onTouched",
  });

  const navigate = useNavigate();
  const returnTo = useAtomValue(returnToAtom);
  const { handleStytchError } = useStytchErrorDrawer();
  const { sendResetPasswordEmail } = usePasswordReset();
  const [isPending, setIsPending] = useState(false);

  const handleSubmit = async (data: ForgotPasswordFormValues): Promise<void> => {
    try {
      setIsPending(true);

      await sendResetPasswordEmail(data.email, returnTo);

      setPasswordReset({ email: data.email });

      void navigate({ to: "/auth/password-reset/check-email" });
    } catch (error) {
      handleStytchError(error, {
        fallback: "Não foi possível enviar o e-mail de redefinição. Tente novamente.",
      });
    } finally {
      setIsPending(false);
    }
  };

  return (
    <AuthPageLayout>
      <FlowHeader>
        <FlowHeader.Actions>
          <FlowHeader.BackButton />
          <FlowHeader.HelpButton />
        </FlowHeader.Actions>
        <FlowHeader.ProgressBar progress={0.33} />
      </FlowHeader>

      <div className="flex flex-col flex-1 p-6 gap-6">
        <div>
          <PageTitle className="pt-0">Esqueci minha senha</PageTitle>
          <PageDescription>
            Por favor, insira seu e-mail para redefinir sua senha
          </PageDescription>
        </div>

        <FormRoot {...form}>
          <FormHandlerSubmit
            handleSubmit={handleSubmit}
            className="flex flex-col flex-1 justify-between w-full"
          >
            <FormField
              control={form.control}
              name="email"
              render={({ field }) => (
                <FormItem>
                  <FormControl>
                    <CpsInput
                      IconLeft={Mail}
                      placeholder="exemplo@mail.com"
                      inputMode="email"
                      type="email"
                      {...field}
                    />
                  </FormControl>
                </FormItem>
              )}
            />

            <AuthFlowContinueButton
              type="submit"
              disabled={!form.formState.isValid}
              loading={isPending}
            />
          </FormHandlerSubmit>
        </FormRoot>
      </div>
    </AuthPageLayout>
  );
};

export const Route = createFileRoute("/auth/password-reset/forgot-password")({
  component: ForgotPasswordPage,
});
